import React, { useCallback, useEffect } from 'react';
import { Button, Col, Input, Row, Select, message } from 'antd';
import { useI18n } from 'hooks/useI18n';
import { formQueryStringFromObject } from 'helpers';
import { SERVER_IP } from 'assets/Config';
import { useDispatch, useSelector } from 'react-redux';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { SMSPageShell, SMSPanel, SMS_PAGE_CREDIT_COUNT } from '../shared';

const SendSingleSMS = () => {
	const { t } = useI18n();
	const customers = useSelector((state) => state?.customerRedux?.customers || []);
	const globalRedux = useSelector((state) => state.globalRedux);
	const [selectedRecipients, setSelectedRecipients] = React.useState([]);
	const [messageText, setMessageText] = React.useState('');
	const dispatch = useDispatch();
	const branchId = globalRedux?.selectedBranch?.id;

	const getCustomers = useCallback(() => {
		const string = formQueryStringFromObject({
			branchId,
		});
		let url = `${SERVER_IP}api/customers?${string}`;
		branchId && dispatch(getApi('GET_CUSTOMERS', url));
	}, [dispatch, branchId]);

	useEffect(() => {
		getCustomers();
	}, [getCustomers]);

	const customerOptions = React.useMemo(
		() =>
			customers.map((customer) => ({
				label: `${customer.customerName} - ${customer.mobile}`,
				value: customer.id,
				mobile: customer.mobile,
				name: customer.customerName,
			})),
		[customers]
	);

	const selectedCustomerDetails = React.useMemo(
		() => customerOptions.filter((option) => selectedRecipients.includes(option.value)),
		[customerOptions, selectedRecipients]
	);

	const handleSend = () => {
		message.success(t('sms.common.send_demo'));
	};

	return (
		<SMSPageShell
			eyebrow={t('navigation.sms')}
			title={t('sms.single.title')}
			subtitle={t('sms.single.subtitle')}
			creditLabel={t('sms.common.available_credits')}
			creditCount={SMS_PAGE_CREDIT_COUNT}
			meta={[
				{ label: t('sms.common.recipients'), value: selectedRecipients.length, helper: t('sms.single.metrics.selected_helper') },
				{ label: t('sms.single.metrics.contacts_loaded'), value: customerOptions.length, helper: branchId ? t('sms.single.metrics.branch_ready') : t('sms.single.metrics.branch_missing') },
				{ label: t('sms.common.message_length'), value: `${messageText.length}/160`, helper: t('sms.single.metrics.personalized_helper') },
			]}
		>
			<Row gutter={[16, 16]}>
				<Col xs={24} xl={14}>
					<SMSPanel title={t('sms.single.recipient_title')} subtitle={t('sms.single.recipient_subtitle')}>
						<div className="sms-composer">
							<Select
								mode="multiple"
								style={{ width: '100%' }}
								value={selectedRecipients}
								showSearch
								placeholder={t('sms.single.select_placeholder')}
								onChange={setSelectedRecipients}
								options={customerOptions}
								optionFilterProp="label"
							/>
							{!branchId ? <div className="sms-empty-state">{t('sms.single.branch_hint')}</div> : null}
							<Input.TextArea placeholder={t('sms.single.message_placeholder')} rows={7} value={messageText} onChange={(event) => setMessageText(event.target.value)} />
						</div>
					</SMSPanel>
				</Col>
				<Col xs={24} xl={10}>
					<SMSPanel
						title={t('sms.common.preview')}
						subtitle={t('sms.single.preview_subtitle')}
						footer={
							<Button className="sms-send-button" type="primary" onClick={handleSend} disabled={!messageText.trim() || !selectedRecipients.length}>
								{t('sms.single.send_button')}
							</Button>
						}
					>
						<div className="sms-preview">
							<span className="sms-preview__eyebrow">{t('sms.single.preview_label')}</span>
							<div className="sms-preview__bubble">{messageText || t('sms.single.preview_placeholder')}</div>
							<div className="sms-recipient-list">
								{selectedCustomerDetails.length ? (
									selectedCustomerDetails.slice(0, 6).map((customer) => (
										<span key={customer.value} className="sms-recipient-pill">
											<strong>{customer.name}</strong>
											{customer.mobile}
										</span>
									))
								) : (
									<div className="sms-empty-state">{t('sms.single.preview_empty')}</div>
								)}
							</div>
						</div>
					</SMSPanel>
				</Col>
			</Row>
		</SMSPageShell>
	);
};

export default SendSingleSMS;
